import { Box } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { statisticsApi } from 'api';
import {
  CloseDialogHandler,
  CrudTable,
  DataGridColumnDefinition,
  Dialog,
  RequisitesInfo,
} from 'components';
import { OrderType, QueryKey } from 'enums';
import { TranslationNamespace } from 'i18n';
import { Requisites, SingleRequisitesStatistics } from 'types';

type Props = {
  open: boolean;
  requisites?: Requisites;
  onClose: CloseDialogHandler;
};

export const RequisitesStatisticsDialog: React.FC<Props> = ({
  open,
  requisites,
  onClose,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.requisites.requisites_list.statistics_dialog',
  });

  const queryResult = useQuery(
    `${QueryKey.RequisitesStatistic}-${requisites?.id!}`,
    () =>
      statisticsApi.getSingleRequisitesStatistics(
        OrderType.Payin,
        requisites?.id!,
      ),
    { enabled: !!requisites?.id },
  );

  const columns = useMemo(
    (): DataGridColumnDefinition<SingleRequisitesStatistics>[] => [
      {
        valueGetter: () => (
          <div>
            <div>
              <i>{t('columns.day')}</i>
            </div>
            <div>
              <i>{t('columns.month')}</i>
            </div>
          </div>
        ),
      },
      {
        header: t('columns.active_count'),
        valueGetter: (item) => (
          <div>
            <div>{item.activeCountPerDay}</div>
            <div>{'—'}</div>
          </div>
        ),
      },
      {
        header: t('columns.active_amount'),
        valueGetter: (item) => (
          <div>
            <div>{item.activeAmountPerDay}</div>
            <div>{'—'}</div>
          </div>
        ),
      },
      {
        header: t('columns.completed_count'),
        valueGetter: (item) => (
          <div>
            <div>{item.completedCountPerDay}</div>
            <div>{item.completedCountPerMonth}</div>
          </div>
        ),
      },
      {
        header: t('columns.completed_amount'),
        valueGetter: (item) => (
          <div>
            <div>{item.completedAmountPerDay}</div>
            <div>{item.completedAmountPerMonth}</div>
          </div>
        ),
      },
      {
        header: t('columns.dispute_count'),
        valueGetter: (item) => (
          <div>
            <div>{item.disputeCountPerDay}</div>
            <div>{item.disputeCountPerMonth}</div>
          </div>
        ),
      },
      {
        header: t('columns.dispute_amount'),
        valueGetter: (item) => (
          <div>
            <div>{item.disputeAmountPerDay}</div>
            <div>{item.disputeAmountPerMonth}</div>
          </div>
        ),
      },
    ],
    [t],
  );

  return (
    <Dialog
      open={open}
      title={t('title')}
      onClose={onClose}
      hideCancel
      maxWidth="xl"
    >
      <Box sx={{ mb: 4 }}>
        <RequisitesInfo requisite={requisites} compact className="tw-mb-4" />
        <CrudTable queryResult={queryResult} columns={columns} hideActions />
      </Box>
    </Dialog>
  );
};
